import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck, Upload, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

export default function RasUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);

  useEffect(() => {
    axios.get(`${API}/ras/upload-pubblico/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Link non valido o scaduto."));
  }, [token]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setBusy(true); setErr("");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await axios.post(`${API}/ras/upload-pubblico/${token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data.count || files.length);
    } catch (e) { setErr(e.response?.data?.detail || "Caricamento non riuscito. Riprova."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5EAEC] flex items-center justify-center p-4"
      style={{ backgroundImage: "radial-gradient(120% 80% at 100% 0%, rgba(122,25,61,0.10), transparent 60%)" }}>
      <div className="max-w-lg w-full">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl" />
          <div className="text-center">
            <div className="font-display text-2xl font-extrabold text-brand">Ginius</div>
            {info?.titolare?.ragione_sociale && <div className="text-sm text-slate-500" data-testid="public-header-studio">{info.titolare.ragione_sociale}</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border-2 border-brand/15 p-7 shadow-sm">
          {err && !info ? (
            <div className="text-center" data-testid="rasup-error"><AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" /><p className="text-slate-600">{err}</p></div>
          ) : !info ? (
            <div className="text-center text-slate-400 py-6"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
          ) : done > 0 ? (
            <div className="text-center" data-testid="rasup-success">
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
              <h1 className="font-display text-2xl font-bold mb-1">
                {done === 1 ? "Documento ricevuto!" : `${done} documenti ricevuti!`}
              </h1>
              <p className="text-slate-600">Grazie. La documentazione è stata trasmessa allo Studio e inserita nel fascicolo del condominio.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-6 h-6 text-brand" /><h1 className="font-display text-xl font-extrabold">Caricamento documento</h1></div>
              <p className="text-slate-500 text-sm mb-4">Condominio <b>{info.condominio_nome}</b>{info.fornitore_nome ? ` · ${info.fornitore_nome}` : ""}</p>
              <div className="bg-[#F5EAEC] rounded-lg p-3 mb-4">
                <div className="text-xs text-slate-500">Documento richiesto</div>
                <div className="font-bold text-brand">{info.documento}</div>
              </div>
              {info.files_count > 0 && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-3">Sono già presenti {info.files_count} file per questa voce. Puoi aggiungerne altri caricando qui sotto.</div>}
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3" data-testid="rasup-upload-error">{err}</div>}
              <label className="block">
                <input type="file" hidden multiple data-testid="rasup-file" accept=".pdf,image/*,.doc,.docx" onChange={(e) => upload(e.target.files)} disabled={busy} />
                <span className={`flex items-center justify-center gap-2 w-full h-14 rounded-xl border-2 border-dashed border-brand/40 cursor-pointer font-bold text-brand ${busy ? "opacity-60" : "hover:bg-[#F5EAEC]"}`}>
                  {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</> : <><Upload className="w-5 h-5" /> Scegli e carica i documenti</>}
                </span>
              </label>
              <p className="text-xs text-slate-400 mt-3 text-center">Puoi selezionare più file in una volta. Formati ammessi: PDF, immagini, Word. Max 20 MB ciascuno.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
